<template>
  <div
    class="chess-date-cell"
    :class="{
      'chess-date-cell--today': isTodayDay,
      'chess-date-cell--weekend': isWeekendDay,
      'chess-date-cell--inactive': isPastDay || isOverPeriodEnd,
      'chess-date-cell--active': !props.disabled,
      'chess-date-cell--selected': isSelected,
      'chess-date-cell--error': isErrorVariant,
      'chess-date-cell--highlight': isHighlightVariant,
    }"
    :data-range-id="props.value?.rangeId"
    :data-selected="isSelected"
  >
    <div v-if="props.dayVisible" class="chess-date-cell__day" :class="`chess-date-cell__day--${props.dayPosition}`">
      {{ dayjs(props.date).format('D') }}
    </div>
    <div class="chess-date-cell__text text-ellipsis" v-html="props.value?.content" />
    <div
      v-if="props.range"
      class="chess-date-cell__range"
      :class="{
        'chess-date-cell__range--first': props.firstInRange,
        'chess-date-cell__range--last': props.lastInRange,
        'chess-date-cell__range--inactive': props.rangeInactive,
      }"
    >
      <div v-if="props.firstInRange" class="paragraph-xxs font-600">#{{ props.value?.rangeId }}</div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T">
import dayjs from 'dayjs';
import { computed, ref } from 'vue';
import type { ChessDateCellType, ChessDateCellVariant } from '@/core/components/interface/Chess/Chess.type';
import type { RangeOfDateOnly } from '@/graphql/generated';

const props = withDefaults(
  defineProps<{
    period?: RangeOfDateOnly;
    date: string;
    value?: ChessDateCellType<T>;
    disabled?: boolean;
    dayVisible?: boolean;
    dayPosition?: 'left' | 'right';
    range?: boolean;
    firstInRange?: boolean;
    lastInRange?: boolean;
    rangeInactive?: boolean;
    variant?: ChessDateCellVariant;
    chessDatesAvailableFrom?: string;
  }>(),
  {
    dayPosition: 'left',
    chessDatesAvailableFrom: dayjs().format('YYYY-MM-DD'),
  },
);

const emit = defineEmits<{
  (event: 'select', value?: ChessDateCellType<T>): void;
  (event: 'unselect', value: string): void;
}>();

const isSelected = ref(false);

const isWeekendDay = computed(() => dayjs(props.date).isoWeekday() > 5);

const isTodayDay = computed(() => props.date === dayjs(new Date()).format('YYYY-MM-DD'));

const isPastDay = computed(() => {
  return props.date < props.chessDatesAvailableFrom;
});

const isOverPeriodEnd = computed(() => {
  return props.period ? props.date > dayjs(props.period.to).format('YYYY-MM-DD') : false;
});

const isCellDisabled = computed(() => props.disabled || isPastDay.value || isOverPeriodEnd.value || !props.value);

const isErrorVariant = computed(() => props.variant === 'error');
const isHighlightVariant = computed(() => props.variant === 'highlight');

const selectCell = () => {
  if (isCellDisabled.value) return;

  emit('select', props.value);

  return props.value;
};

const unselectCell = () => {
  if (isCellDisabled.value) return;

  emit('unselect', props.date);
};

const toggleIsSelected = (value: boolean) => {
  if (isCellDisabled.value) return;

  isSelected.value = value;
};

defineExpose({
  selectCell,
  unselectCell,
  toggleIsSelected,
});
</script>

<style scoped lang="scss">
.chess-date-cell {
  $root: &;

  position: relative;

  display: flex;
  flex-direction: column;
  align-items: flex-end;

  height: 100%;
  padding: 8px;

  border-radius: inherit;

  user-select: none;

  @include xs {
    padding: 4px;
  }

  &__day,
  &__text {
    position: relative;
    z-index: 1;
  }

  &__day {
    width: 100%;

    font-weight: 600;

    @include sm-and-down {
      font-size: 12px;
      line-height: 16px;
    }

    &--left {
      text-align: left;
    }

    &--right {
      text-align: right;
    }
  }

  &__text {
    width: 100%;
    margin-top: auto;

    font-weight: 500;

    @include sm-and-down {
      font-size: 9px;
      line-height: 12px;
    }
  }

  &__range {
    position: absolute;
    inset: -1px;

    padding: 3px 7px;

    color: $primary-color;

    border-top: 1px solid $primary-color;
    border-bottom: 1px solid $primary-color;

    &--first {
      right: -2px;

      border-left: 1px solid $primary-color;
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
    }

    &--last {
      left: -2px;

      border-right: 1px solid $primary-color;
      border-top-right-radius: 10px;
      border-bottom-right-radius: 10px;
    }

    &:not(&--first):not(&--last) {
      right: -2px;
      left: -2px;
    }

    &--first:is(&--last) {
      right: 0;
      left: 0;
    }

    &--inactive {
      color: $text-inactive-color;

      background-color: $bg-light-color;
      border-color: $border-color-light;
    }
  }

  &--weekend {
    background-color: $primary-light-color;
  }

  &--error {
    color: $alert-color;

    background-color: $alert-bg-color;
  }

  &--highlight {
    box-shadow: inset 0 0 0 1px $warning-color;
  }

  &--inactive {
    color: $text-inactive-color;

    pointer-events: none;
  }

  &--active {
    cursor: pointer;
  }

  &--active:not(&--selected) {
    @include hover {
      background-color: $primary-tonal-color;
    }
  }

  &--selected {
    color: $text-white-color;

    background-color: $primary-color;

    #{$root}__range {
      color: $text-white-color;
    }
  }
}
</style>
